import React, { useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import { Outlet, useNavigate, useParams } from 'react-router-dom'
import { useGetUsersByAgency } from '../../../api/agencies/useGetUsersByAgency'
import { Button, Table } from '@demandlocal/components'
import { useSelector } from 'react-redux'
import { currentUserSelector } from '../../../store/selectors/userSelector'
import { ReactComponent as ActionEdit } from '../../../images/icons/action_edit.svg'
import { ReactComponent as ActionDelete } from '../../../images/icons/action_delete.svg'
import toast from 'react-hot-toast'
import { queryClient } from '../../../api/queryClient'
import { useDeleteAgencyUser } from '../../../api/agencies/users/useDeleteUser'

type UserRowShape = {
  id: number
  first_name: string
  last_name: string
  email: string
  user_role: string
  role_id: number
  total_dealerships: number
  assigned_dealerships: number
}
const userColumnHelper = createColumnHelper<UserRowShape>()

export const AgencyUsers = (): JSX.Element => {
  const { agency_id } = useParams()
  const navigate = useNavigate()
  const user = useSelector(currentUserSelector)
  const { mutateAsync: deleteUser } = useDeleteAgencyUser()

  const dealershipColumns = useMemo(
    () => [
      userColumnHelper.accessor('first_name', {
        header: 'First Name',
        cell: ({ getValue }) => getValue(),
      }),
      userColumnHelper.accessor('last_name', {
        header: 'Last Name',
        cell: ({ getValue }) => getValue(),
      }),
      userColumnHelper.accessor('email', {
        header: 'Email',
        cell: ({ getValue }) => getValue(),
      }),
      userColumnHelper.accessor('user_role', {
        header: 'Role',
        cell: ({ getValue }) => getValue(),
      }),
      userColumnHelper.display({
        header: 'Assigned Dealerships',
        cell: (info) =>
          `${info.row.original.assigned_dealerships}/${info.row.original.total_dealerships}`,
      }),
      userColumnHelper.display({
        id: 'actions',
        header: () => 'Actions',
        cell: (info) =>
          Number(user?.role_id) < 3 &&
          Number(info.row.original.role_id) >= 2 && (
            <div className={'flex flex-row'}>
              <ActionDelete
                className={'cursor-pointer'}
                onClick={async () => {
                  if (confirm('Are you sure you want to delete this user?')) {
                    const toastId = toast.loading('Deleting user...')
                    await deleteUser({
                      user_id: info.row.original.id.toString(),
                    })
                    await queryClient.invalidateQueries(['admin', 'user'])
                    toast.success('User deleted successfully', { id: toastId })
                  }
                }}
              />
              <ActionEdit
                stroke={'#0932C2'}
                className={'ml-4 cursor-pointer'}
                onClick={() => {
                  navigate(`${info.row.original.id}/edit`)
                }}
              />
            </div>
          ),
      }),
    ],
    [deleteUser, navigate, user?.role_id],
  )

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-row justify-end">
        <Button onClick={() => navigate('new')}>Add User</Button>
      </div>
      <Table
        emptyLabel={'No associated users'}
        columns={dealershipColumns}
        filters={{ agency_id: { value: agency_id }, user_role: { value: user?.role_id } }}
        fetchFn={useGetUsersByAgency}
      />
      <Outlet />
    </div>
  )
}

export default AgencyUsers
